import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '@/services/api';

interface SignInData {
  token: string;
  name: string;
  email: string;
  user_id: number;
  company_name: string;
  email_verified: boolean;
  first_time: boolean;
  approver_allowed: boolean;
  butterfly: boolean;
  mayfly_allowed: boolean;
  mayfly: boolean;
  show_revision_tutorial: boolean;
}

interface SignInResponse {
  data: SignInData;
  http_code: number;
  message: string;
}

interface AuthState {
  token: string | null;
  name: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const TOKEN_KEY = 'auth_token';
const NAME_KEY = 'auth_name';

const initialState: AuthState = {
  token: localStorage.getItem(TOKEN_KEY),
  name: localStorage.getItem(NAME_KEY),
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/sign_in',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await authApi.get<SignInResponse>('/sign_in', {
        params: {
          email,
          password,
          product_id: Number(import.meta.env.VITE_PRODUCT_ID ?? 6),
        },
      });
      const token = res.data.data?.token;
      if (!token) return rejectWithValue('No token received from server');
      return { token, name: res.data.data?.name ?? '' };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string; message?: string; error?: string } } };
      const msg =
        axiosErr.response?.data?.detail ??
        axiosErr.response?.data?.message ??
        axiosErr.response?.data?.error ??
        'Invalid email or password';
      return rejectWithValue(msg);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.name = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(NAME_KEY);
    },
    clearAuthError(state) {
      state.error = null;
    },
    setToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem(TOKEN_KEY, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.name = action.payload.name;
        state.isAuthenticated = true;
        localStorage.setItem(TOKEN_KEY, action.payload.token);
        localStorage.setItem(NAME_KEY, action.payload.name);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearAuthError, setToken } = authSlice.actions;
export default authSlice.reducer;
